import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import useAdminNotifications from "../../hooks/useAdminNotifications";
import {
  formatTimeAgo,
  getNotificationIcon,
  getNotificationNavigationLink,
  isNotificationUnread,
  markAllNotificationsRead,
  markNotificationRead,
} from "../utils/adminNotifications";

export default function NotificationBell({
  notifications: notificationsProp,
  unreadCount: unreadCountProp,
  getNotificationIcon: getNotificationIconProp = getNotificationIcon,
  viewAllPath = "/dashboard/notifications",
  dropdownId = "admin-notifications-dropdown",
  onMarkRead,
  onMarkAllRead,
  formatTimeAgo: formatTimeAgoProp = formatTimeAgo,
  isNotificationUnread: isNotificationUnreadProp = isNotificationUnread,
}) {
  const navigate = useNavigate();
  const adminNotifications = useAdminNotifications();
  const notifications = notificationsProp ?? adminNotifications.notifications;
  const unreadCount = unreadCountProp ?? adminNotifications.unreadCount;
  const [show, setShow] = useState(false);
  const dropdownRef = useRef(null);

  const latestNotifications = notifications.slice(0, 10);

  const handleMarkRead = onMarkRead ?? markNotificationRead;
  const handleMarkAllRead = onMarkAllRead ?? markAllNotificationsRead;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShow(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification) => {
    if (isNotificationUnreadProp(notification)) {
      try {
        await handleMarkRead(notification.id);
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }

    setShow(false);
    const link = getNotificationNavigationLink(notification);
    if (link) {
      navigate(link);
    }
  };

  const handleMarkAllReadClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await handleMarkAllRead();
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  const handleViewAll = (e) => {
    e.preventDefault();
    setShow(false);
    navigate(viewAllPath);
  };

  return (
    <li className="nav-item nav-item-notifications" ref={dropdownRef}>
      <Dropdown show={show} onToggle={(isOpen) => setShow(isOpen)} align="end">
        <Dropdown.Toggle
          as="button"
          className="nav-link text-black border-0 bg-transparent nav-notifications-toggle d-flex align-items-center"
          id={dropdownId}
          onClick={() => setShow((prev) => !prev)}
          style={{ boxShadow: "none" }}
          aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ""}`}
        >
          <span className="notifications-bell-icon-wrap">
            <i className="material-icons">notifications</i>
            {unreadCount > 0 && (
              <span className="notifications-bell-badge">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu className="notifications-dropdown dropdown-menu-end shadow">
          <div className="notifications-dropdown-header">
            <div>
              <h6 className="notifications-dropdown-title">Notifications</h6>
              {unreadCount > 0 && (
                <span className="notifications-dropdown-subtitle">
                  {unreadCount} unread
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                className="notifications-mark-all-btn"
                onClick={handleMarkAllReadClick}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="notifications-dropdown-list">
            {latestNotifications.length === 0 ? (
              <div className="notifications-dropdown-empty">
                <i className="material-icons">notifications_none</i>
                <p>No notifications yet</p>
                <span>We&apos;ll let you know when something happens.</span>
              </div>
            ) : (
              latestNotifications.map((notification) => {
                const unread = isNotificationUnreadProp(notification);

                return (
                  <button
                    key={notification.id}
                    type="button"
                    className={`notifications-dropdown-item w-100 border-0 text-start${
                      unread ? " notifications-dropdown-item--unread" : ""
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notifications-dropdown-item-icon">
                      <span>{getNotificationIconProp(notification.type)}</span>
                    </div>
                    <div className="notifications-dropdown-item-content">
                      <p className="notifications-dropdown-item-title">
                        {notification.title}
                      </p>
                      <p className="notifications-dropdown-item-message">
                        {notification.message}
                      </p>
                      <small className="notifications-dropdown-item-time">
                        {formatTimeAgoProp(notification.createdAt)}
                      </small>
                    </div>
                    {unread && <span className="notifications-unread-dot" />}
                  </button>
                );
              })
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notifications-dropdown-footer">
              <button
                type="button"
                className="notifications-view-all-btn"
                onClick={handleViewAll}
              >
                View all notifications
              </button>
            </div>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </li>
  );
}
